// <s-motion> — ALBRÄ's shared, calm motion layer (homepage, collections, product, members, search, pages).
// 1. Reveal: one IntersectionObserver adds `.is-visible` once to every [data-reveal] / [data-reveal-words]
//    element and staggers its [data-reveal-child] descendants by 80ms (max 5 steps) via --reveal-delay.
//    The root margin starts below the sticky header, so nothing animates behind it.
// 2. Drift: inside a [data-drift-scope], each [data-drift="±px"] element moves horizontally with scroll,
//    at most its own value. The rAF-throttled scroll listener exists only while the scope is on screen,
//    only at ≥ 64rem and never under reduced motion.
// Pre-reveal states live in config/styles.css behind `s-motion:defined`: no JS means nothing is hidden.
const STAGGER = 80;
const MAX_STAGGER_STEPS = 5;

class Motion extends HTMLElement {
  #revealObserver = null;
  #driftObserver = null;
  #controller = null;
  #frame = 0;
  #reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
  #desktop = window.matchMedia('(min-width: 64rem)');

  connectedCallback() {
    this.#controller = new AbortController();
    this.#setupReveal();
    this.#setupDrift();
  }

  disconnectedCallback() {
    this.#revealObserver?.disconnect();
    this.#driftObserver?.disconnect();
    this.#controller?.abort();
    cancelAnimationFrame(this.#frame);
  }

  #headerOffset() {
    const value = getComputedStyle(document.documentElement).getPropertyValue('--albra-header-bottom');
    const parsed = Number.parseFloat(value);
    return Number.isFinite(parsed) ? Math.max(0, Math.round(parsed)) : 64;
  }

  #setupReveal() {
    const targets = this.querySelectorAll('[data-reveal], [data-reveal-words]');
    for (const target of targets) {
      target.querySelectorAll('[data-reveal-child]').forEach((child, index) => {
        child.style.setProperty('--reveal-delay', `${Math.min(index, MAX_STAGGER_STEPS) * STAGGER}ms`);
      });
    }

    this.#revealObserver = new IntersectionObserver(
      (entries, observer) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        }
      },
      { rootMargin: `-${this.#headerOffset()}px 0px -8% 0px`, threshold: 0.08 }
    );
    targets.forEach((target) => this.#revealObserver.observe(target));
  }

  #setupDrift() {
    const scope = this.querySelector('[data-drift-scope]');
    if (!scope) return;
    const items = [...scope.querySelectorAll('[data-drift]')];
    if (!items.length) return;

    const signal = this.#controller.signal;
    let listening = false;
    const enabled = () => this.#desktop.matches && !this.#reduced.matches;

    const update = () => {
      this.#frame = 0;
      const rect = scope.getBoundingClientRect();
      const viewport = window.innerHeight || 1;
      // -1 while the scope enters at the bottom, 0 when centred, +1 as it leaves at the top.
      const progress = Math.max(-1, Math.min(1, (viewport / 2 - (rect.top + rect.height / 2)) / (viewport / 2 + rect.height / 2)));
      for (const item of items) {
        const range = Number.parseFloat(item.dataset.drift) || 0;
        item.style.setProperty('--drift', `${(progress * range).toFixed(2)}px`);
      }
    };
    const onScroll = () => {
      if (!this.#frame) this.#frame = requestAnimationFrame(update);
    };
    const start = () => {
      if (listening || !enabled()) return;
      listening = true;
      window.addEventListener('scroll', onScroll, { passive: true, signal });
      update();
    };
    const stop = () => {
      if (!listening) return;
      listening = false;
      window.removeEventListener('scroll', onScroll);
    };
    const reset = () => {
      if (enabled()) return;
      stop();
      items.forEach((item) => item.style.removeProperty('--drift'));
    };

    this.#driftObserver = new IntersectionObserver((entries) => {
      for (const entry of entries) entry.isIntersecting ? start() : stop();
    });
    this.#driftObserver.observe(scope);
    this.#reduced.addEventListener('change', reset, { signal });
    this.#desktop.addEventListener('change', reset, { signal });
  }
}

if (!customElements.get('s-motion')) customElements.define('s-motion', Motion);
